/**
 * Avatec Marine UAE E-mail Signature Template
 *
 * Cross-client compatible HTML email signature generator.
 * Tested for: Outlook Desktop 2007-2024, New Outlook, Gmail (web/mobile),
 * Apple Mail, Thunderbird, Yahoo Mail, Outlook.com, Samsung Mail, Android default mail.
 */

import { getLink, escapeHtml } from '../helpers';

// Palette of the customer document this layout reproduces
const UAE_COLORS = {
  identity: '#156082',
  detail: '#00B0F0',
  link: '#467886',
  divider: '#46B1E1'
};

// CSS font-family value used by every cell
const UAE_FONT_STACK = "Aptos, Calibri, 'Segoe UI', Arial, Helvetica, sans-serif";

// Legacy <font face> value - quotes inside the attribute break old clients
const UAE_FONT_FACE = 'Aptos, Calibri, Arial, Helvetica, sans-serif';

// public/logo-avatec.png is 227x216, so this is its true aspect at the document height
const UAE_LOGO_WIDTH = 91;
const UAE_LOGO_HEIGHT = 87;

// Gap above the e-mail and website line. The source document uses a full blank
// paragraph here, which reads as too much space, so it is tightened to a plain break.
const UAE_CONTACT_GAP_HEIGHT = 8;

// Divider height of the source document, less the space taken out of the gap above,
// grown when the details are taller
const UAE_DIVIDER_MIN_HEIGHT = 142;

// Shared cell styles, kept in one place because every line repeats them
const UAE_NAME_CELL_STYLE = `padding:0 0 0 27px; font-family:${UAE_FONT_STACK}; font-size:14pt; line-height:23px; mso-line-height-rule:exactly; font-weight:bold; color:${UAE_COLORS.identity}; mso-color-alt:${UAE_COLORS.identity}; white-space:nowrap;`;
const UAE_IDENTITY_CELL_STYLE = `padding:0 0 0 27px; font-family:${UAE_FONT_STACK}; font-size:11pt; line-height:18px; mso-line-height-rule:exactly; font-weight:normal; color:${UAE_COLORS.identity}; mso-color-alt:${UAE_COLORS.identity}; white-space:nowrap;`;
const UAE_DETAIL_CELL_STYLE = `padding:0 0 0 27px; font-family:${UAE_FONT_STACK}; font-size:11pt; line-height:18px; mso-line-height-rule:exactly; font-weight:normal; color:${UAE_COLORS.detail}; mso-color-alt:${UAE_COLORS.detail}; white-space:nowrap;`;
const UAE_LINK_CELL_STYLE = `font-family:${UAE_FONT_STACK}; font-size:11pt; line-height:18px; mso-line-height-rule:exactly; white-space:nowrap;`;

// Default values for Avatec Marine UAE template
const AVATEC_UAE_DEFAULTS = {
  company: 'AVATEC Marine',
  additionalText: '',
  websiteUrl: 'https://www.avatecmarine.com',
  websiteDisplay: 'www.avatecmarine.com',
  logoUrl: '/logo-avatec.png',
  email: 'info@avatecmarine.com',
  hubs: 'UAE | Turkey | Netherlands | Estonia | Finland | Germany | India | Lithuania | Morocco | Azerbaijan | China | Panama',
  rightRow1: {
    label: '',
    value: 'Plot no. 246-5905, Saud Talal warehouse, No. 8'
  },
  rightRow2: {
    label: '',
    value: 'AL Qusais-3, Dubai – UAE'
  },
  rightRow3: {
    label: 'Mobile',
    value: 'UAE +971 565780909 / India +91 82919 91298'
  },
  rightRow4: {
    label: 'Tel',
    value: '+971 4 552 7694'
  }
};

/**
 * Normalizes any value into a trimmed string so a missing field never throws
 * @param {*} value - The value to normalize
 * @returns {string} Trimmed text, empty when the value is nullish
 */
const text = (value) => String(value ?? '').trim();

/**
 * Formats a contact row into a single detail line
 * @param {Object} row - Contact row holding an optional label and value
 * @returns {string} "Label: value", the only part present, or an empty string
 */
const formatDetailLine = (row) => {
  const label = text(row?.label);
  const value = text(row?.value);

  if (label !== '' && value !== '') {
    return `${label}: ${value}`;
  }

  return label !== '' ? label : value;
};

/**
 * Builds one row of the detail table
 * @param {string} cellStyle - Style attribute of the cell
 * @param {string} color - Color repeated on the legacy <font> wrapper
 * @param {string} content - Ready to render, already escaped content
 * @returns {string} Table row markup
 */
const buildLineRow = (cellStyle, color, content) => `
          <tr>
            <td style="${cellStyle}">
              <font face="${UAE_FONT_FACE}" color="${color}">${content}</font>
            </td>
          </tr>`;

/**
 * Renders a value as an anchor when getLink recognized it, as plain text otherwise
 * @param {Object} link - Result of getLink for the underlying value
 * @param {string} expectedType - The only link type allowed to become an anchor
 * @param {string} display - Text shown to the reader
 * @param {boolean} newTab - Whether the anchor opens in a new tab
 * @returns {string} Anchor markup, or plain markup when there is no usable link
 */
const renderLinkedValue = (link, expectedType, display, newTab) => {
  const caption = `<font face="${UAE_FONT_FACE}" color="${UAE_COLORS.link}">${escapeHtml(display)}</font>`;

  // An unrecognized value stays plain text, href="#" is never emitted
  if (link.type !== expectedType) {
    return `<span style="color:${UAE_COLORS.link}; mso-color-alt:${UAE_COLORS.link};">${caption}</span>`;
  }

  const target = newTab ? ' target="_blank"' : '';

  return `<a href="${escapeHtml(link.href)}"${target} style="color:${UAE_COLORS.link}; mso-color-alt:${UAE_COLORS.link}; text-decoration:underline;">${caption}</a>`;
};

/**
 * Generate HTML for Avatec Marine UAE email signature template
 * @param {Object} formData - User input data for the email signature
 * @param {Object} switchStates - State of toggle switches for optional content
 * @returns {string} Generated HTML
 */
const generateAvatecUaeTemplate = (formData, switchStates) => {
  // Determine which logo source to use
  const logoSource = formData.logoType === 'base64'
    ? formData.logoBase64
    : formData.logoUrl;

  const fullName = text(formData.fullName);
  const position = text(formData.position);
  const company = text(formData.company);

  // Cyan contact lines, rendered as plain text: getLink mangles composed lines
  const detailLines = [
    formatDetailLine(formData.rightRow1),
    formatDetailLine(formData.rightRow2),
    formatDetailLine(formData.rightRow3),
    formatDetailLine(formData.rightRow4)
  ].filter((line) => line !== '');

  // E-mail and website are the only linked values of this layout
  const email = text(formData.email);
  const websiteUrl = text(formData.websiteUrl);
  const emailLink = getLink(email);
  const siteLink = getLink(websiteUrl);

  // Website is shown without its scheme, exactly like in the source document
  const websiteDisplay = websiteUrl
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/\/+$/, '');

  const emailCell = email === ''
    ? ''
    : `<td valign="middle" style="${UAE_LINK_CELL_STYLE}">${renderLinkedValue(emailLink, 'email', email, false)}</td>`;
  const websiteCell = websiteDisplay === ''
    ? ''
    : `<td valign="middle" style="${UAE_LINK_CELL_STYLE}">${renderLinkedValue(siteLink, 'website', websiteDisplay, true)}</td>`;
  const gapCell = emailCell !== '' && websiteCell !== ''
    ? `<td width="40" style="width:40px; font-size:1px; line-height:18px; mso-line-height-rule:exactly;">&#160;</td>`
    : '';
  const hasContactRow = emailCell !== '' || websiteCell !== '';

  // Country strip accepts the new " | " form and the legacy " - " form alike
  const hubList = text(formData.hubs)
    .split(/\s*\|\s*|\s+-\s+/)
    .map((hub) => hub.trim())
    .filter(Boolean);
  const showHubs = Boolean(switchStates?.hubs) && hubList.length > 0;
  const remainingHubs = hubList
    .slice(1)
    .map((hub) => escapeHtml(hub))
    .join(' | ');

  // The divider spans the upper block, so it follows the lines actually emitted
  const detailColumnHeight = 23 +
    (position === '' ? 0 : 18) +
    18 +
    (detailLines.length * 18) +
    (hasContactRow ? UAE_CONTACT_GAP_HEIGHT + 18 : 0);
  const dividerHeight = Math.max(UAE_DIVIDER_MIN_HEIGHT, detailColumnHeight);

  const logoImage = `<img src="${escapeHtml(logoSource)}" width="${UAE_LOGO_WIDTH}" height="${UAE_LOGO_HEIGHT}" alt="${escapeHtml(company)}" style="display:block; width:${UAE_LOGO_WIDTH}px; height:${UAE_LOGO_HEIGHT}px; border:0; outline:none; -ms-interpolation-mode:bicubic;" border="0" />`;
  const logoBlock = siteLink.type === 'website'
    ? `<a href="${escapeHtml(siteLink.href)}" target="_blank" style="text-decoration:none; border:0;">${logoImage}</a>`
    : logoImage;

  const nameRow = buildLineRow(UAE_NAME_CELL_STYLE, UAE_COLORS.identity, escapeHtml(fullName));
  const positionRow = position === ''
    ? ''
    : buildLineRow(UAE_IDENTITY_CELL_STYLE, UAE_COLORS.identity, escapeHtml(position));
  const companyRow = buildLineRow(UAE_IDENTITY_CELL_STYLE, UAE_COLORS.identity, escapeHtml(company));
  const detailRows = detailLines
    .map((line) => buildLineRow(UAE_DETAIL_CELL_STYLE, UAE_COLORS.detail, escapeHtml(line)))
    .join('');

  const contactRow = hasContactRow ? `

          <!-- Break above the contact line -->
          <tr>
            <td height="${UAE_CONTACT_GAP_HEIGHT}" style="height:${UAE_CONTACT_GAP_HEIGHT}px; font-size:1px; line-height:${UAE_CONTACT_GAP_HEIGHT}px; mso-line-height-rule:exactly;">&#160;</td>
          </tr>

          <!-- E-mail and website line -->
          <tr>
            <td style="padding:0 0 0 27px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
                <tr>${emailCell}${gapCell}${websiteCell}</tr>
              </table>
            </td>
          </tr>` : '';

  // The blank paragraph only exists to separate the strip, so both appear together
  const hubsBlock = showHubs ? `

    <!-- Empty paragraph from the source document -->
    <tr>
      <td colspan="3" height="19" style="height:19px; font-size:1px; line-height:19px; mso-line-height-rule:exactly;">&#160;</td>
    </tr>

    <!-- Country strip, full width, no indent. It is a single line by design, so it is kept
         unwrappable: the list only fits 640px in Aptos or Calibri, and any fallback font is
         wide enough to push the last entries onto a second line. -->
    <tr>
      <td colspan="3" nowrap="nowrap" style="padding:0; font-family:${UAE_FONT_STACK}; font-size:11pt; line-height:18px; mso-line-height-rule:exactly; white-space:nowrap; color:${UAE_COLORS.identity}; mso-color-alt:${UAE_COLORS.identity};">
        <font face="${UAE_FONT_FACE}" color="${UAE_COLORS.identity}"><strong style="color:${UAE_COLORS.identity}; mso-color-alt:${UAE_COLORS.identity};">${escapeHtml(hubList[0])}</strong>${remainingHubs === '' ? '' : ` | ${remainingHubs}`}</font>
      </td>
    </tr>` : '';

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="format-detection" content="telephone=no" />
  <!--[if !mso]><!-->
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <!--<![endif]-->
  <title>Email Signature</title>

  <!--[if mso]>
  <style type="text/css">
    table {border-collapse:collapse !important; border-spacing:0 !important; margin:0 !important;}
  </style>
  <noscript>
  <xml>
    <o:OfficeDocumentSettings>
      <o:PixelsPerInch>96</o:PixelsPerInch>
      <o:AllowPNG/>
    </o:OfficeDocumentSettings>
  </xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin:0; padding:0; background-color:#FFFFFF; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%;" bgcolor="#FFFFFF">

  <!-- ===================== AVATEC MARINE UAE SIGNATURE ===================== -->
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="640" style="border-collapse:collapse; width:640px; background-color:#FFFFFF;" bgcolor="#FFFFFF">

    <!-- Upper block: logo | divider | personal and contact details -->
    <tr>
      <!-- Logo column -->
      <td width="116" valign="top" style="width:116px; padding:0 0 0 8px; font-size:0; line-height:0;">
        ${logoBlock}
      </td>

      <!-- Vertical divider (1pt, as tall as the detail column) -->
      <td width="1" valign="top" bgcolor="${UAE_COLORS.divider}" style="width:1px; padding:0; font-size:0; line-height:0; background-color:${UAE_COLORS.divider};">
        <div style="width:1px; margin:0; height:${dividerHeight}px; font-size:1px; line-height:${dividerHeight}px; mso-line-height-rule:exactly;">&#160;</div>
      </td>

      <!-- Detail column -->
      <td width="523" valign="top" style="width:523px; padding:0;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="523" style="border-collapse:collapse; width:523px;">${nameRow}${positionRow}${companyRow}${detailRows}${contactRow}
        </table>
      </td>
    </tr>${hubsBlock}
  </table>
  <!-- =================== END AVATEC MARINE UAE SIGNATURE =================== -->

</body>
</html>`;
};

// Export both the template generator and its default values
export { AVATEC_UAE_DEFAULTS };
export default generateAvatecUaeTemplate;
